/** Minimal vessel fields for Boat Club storage location checks. */
export interface BoatClubLocationInput {
  dryDockLocationId?: string | null;
  slipId?: string | null;
}

/** Vessel slip/location fields for admin list and launch board display. */
export interface VesselSlipLocationInput {
  permanentLocationName?: string | null;
  temporaryLocationName?: string | null;
  temporaryLocationId?: string | null;
  lease?: { slipName?: string | null } | null;
}

export interface VesselSlipDisplayParts {
  permanent: string | null;
  temporary: string | null;
}

/** True when a Boat Club vessel has a permanent wet slip or dry-storage slot assigned. */
export function vesselHasBoatClubAssignedLocation(vessel: BoatClubLocationInput | null | undefined): boolean {
  if (!vessel) {
    return false;
  }
  const dry = vessel.dryDockLocationId?.trim();
  const slip = vessel.slipId?.trim();
  return !!(dry || slip);
}

export function vesselHasTemporaryLocation(
  vessel: VesselSlipLocationInput | null | undefined
): boolean {
  return !!vessel?.temporaryLocationName?.trim() || !!vessel?.temporaryLocationId;
}

/** Splits permanent and temporary slip labels for selective UI highlighting. */
export function getVesselSlipDisplayParts(
  vessel: VesselSlipLocationInput | null | undefined,
  options?: { preferLeaseSlip?: boolean }
): VesselSlipDisplayParts {
  if (!vessel) {
    return { permanent: null, temporary: null };
  }

  const temp = vessel.temporaryLocationName?.trim() || null;
  const hasTemp = vesselHasTemporaryLocation(vessel);
  const leaseSlip = vessel.lease?.slipName?.trim() || null;

  if (options?.preferLeaseSlip !== false && leaseSlip) {
    return { permanent: leaseSlip, temporary: null };
  }

  const perm = vessel.permanentLocationName?.trim() || leaseSlip || null;
  return {
    permanent: perm,
    temporary: hasTemp ? temp : null,
  };
}

export function formatVesselSlipName(parts: VesselSlipDisplayParts): string | null {
  const { permanent, temporary } = parts;
  if (temporary && permanent) {
    return `${permanent} (temp: ${temporary})`;
  }
  if (temporary) {
    return `Temp: ${temporary}`;
  }
  return permanent;
}

export function formatLaunchSlipName(parts: VesselSlipDisplayParts): string {
  const { permanent, temporary } = parts;
  if (temporary && permanent) {
    return `${permanent} · Temp: ${temporary}`;
  }
  if (temporary) {
    return temporary;
  }
  return permanent || '';
}

export interface LaunchSlipDisplayParts extends VesselSlipDisplayParts {
  returned: string | null;
  returnedIsTemporary: boolean;
  showReturned: boolean;
  hasDisplay: boolean;
}

export function getLaunchSlipDisplayParts(
  vessel: VesselSlipLocationInput | null | undefined,
  options?: {
    status?: string | null;
    returnLocationName?: string | null;
    returnIsTemporaryLocation?: boolean | null;
  }
): LaunchSlipDisplayParts {
  const { permanent, temporary } = getVesselSlipDisplayParts(vessel, { preferLeaseSlip: true });
  const slipString = formatLaunchSlipName({ permanent, temporary });

  let returned: string | null = null;
  let returnedIsTemporary = false;
  let showReturned = false;

  const status = (options?.status || '').toUpperCase();
  if (status === 'RETURNED' && options?.returnLocationName?.trim()) {
    returned = options.returnLocationName.trim();
    returnedIsTemporary = !!options.returnIsTemporaryLocation;
    showReturned = !!(slipString && returned !== slipString);
  }

  return {
    permanent,
    temporary,
    returned,
    returnedIsTemporary,
    showReturned,
    hasDisplay: !!(permanent || temporary || returned),
  };
}
